import { useState, useEffect, useRef } from 'react';
import { api, usePortfolio } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import PortfolioSelector from './PortfolioSelector';
import UnitSearch from './UnitSearch';
import MobileMenu from './MobileMenu';

export default function AgentDashboard({ user, onLogout }) {
  const { activePortfolioId, portfolios } = usePortfolio();
  const portfolioRef = useRef(null);
  const searchRef = useRef(null);
  
  const [units, setUnits] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showAllUnits, setShowAllUnits] = useState(false);
  
  // Notification preferences
  const [notifyRequestStatus, setNotifyRequestStatus] = useState(true);
  const [notifyVacantReady, setNotifyVacantReady] = useState(true);
  
  // Request form
  const [newStatus, setNewStatus] = useState('Lease-in-Progress');
  const [tenantName, setTenantName] = useState('');
  const [subsidyProgram, setSubsidyProgram] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [moveInDate, setMoveInDate] = useState('');

  // Load data when active portfolio changes
  useEffect(() => {
    if (activePortfolioId) {
      loadData();
      loadNotificationPreferences();
    }
  }, [activePortfolioId]);

  const loadData = async () => {
    if (!activePortfolioId) return;
    
    setLoading(true);
    try {
      const [unitsRes, requestsRes] = await Promise.all([
        api.get(`/units?portfolio_id=${activePortfolioId}`),
        api.get(`/status-requests?portfolio_id=${activePortfolioId}`)
      ]);
      
      setUnits(unitsRes.data);
      setMyRequests(requestsRes.data.filter(r => r.agent_email === user.email));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await api.get('/notification-preferences');
      setNotifyRequestStatus(response.data.notify_request_status);
      setNotifyVacantReady(response.data.notify_vacant_ready);
    } catch (error) {
      console.error('Failed to load notification preferences');
    }
  };

  const handleUpdateNotifications = async () => {
    try {
      await api.put('/notification-preferences', {
        notify_request_status: notifyRequestStatus,
        notify_vacant_ready: notifyVacantReady
      });
      toast.success('Notification preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const openRequestModal = (unit) => {
    setSelectedUnit(unit);
    setNewStatus('Lease-in-Progress');
    setTenantName('');
    setSubsidyProgram('');
    setRentAmount('');
    setMoveInDate('');
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!activePortfolioId) {
      toast.error('No portfolio selected');
      return;
    }
    
    try {
      await api.post('/status-requests', {
        unit_id: selectedUnit.id,
        new_status: newStatus,
        tenant_name: tenantName,
        subsidy_program: subsidyProgram || null,
        rent_amount: parseFloat(rentAmount),
        move_in_date: moveInDate,
        portfolio_id: activePortfolioId
      });
      
      toast.success('Status change request submitted successfully');
      setShowRequestModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  // Group units by address/building
  const unitsByAddress = units.reduce((acc, unit) => {
    const address = unit.address || 'No Address';
    if (!acc[address]) {
      acc[address] = [];
    }
    acc[address].push(unit);
    return acc;
  }, {});

  // Filter to show vacant-ready units or all units based on toggle
  const getDisplayUnits = (addressUnits) => {
    if (showAllUnits) {
      return addressUnits;
    }
    return addressUnits.filter(u => u.status === 'Vacant-Ready');
  };

  if (loading && !activePortfolioId) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="agent-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 data-testid="dashboard-title" className="brand-title">
            <img src="/favicon.png" alt="" className="brand-mark" />
            <span className="brand-text">Unit Relay</span>
            <span className="role-badge" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>Agent</span>
          </h1>
          <PortfolioSelector ref={portfolioRef} userRole="agent" />
          <UnitSearch ref={searchRef} userRole="agent" />
        </div>
        <div className="header-actions">
          <span className="user-email" data-testid="user-email">{user.email}</span>
          <button className="btn-secondary" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
        <MobileMenu
          portfolioRef={portfolioRef}
          searchRef={searchRef}
          userRole="agent"
          userEmail={user.email}
          onLogout={onLogout}
        />
      </div>

      {!activePortfolioId ? (
        <div className="dashboard-content">
          <div className="empty-state" style={{ padding: '60px', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '16px' }}>No Portfolio Selected</h2>
            <p style={{ color: '#64748b' }}>Join a portfolio using the token provided by your portfolio owner.</p>
          </div>
        </div>
      ) : loading ? (
        <div className="loading-screen" style={{ minHeight: '400px' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : (
      <div className="dashboard-content">
        <Tabs defaultValue="units" className="dashboard-tabs">
          <TabsList className="tabs-nav" data-testid="agent-tabs">
            <TabsTrigger value="units" data-testid="units-tab">Available Units</TabsTrigger>
            <TabsTrigger value="requests" data-testid="my-requests-tab">My Requests</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="units">
        {/* Portfolio Units */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Available Units</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span className="user-email" data-testid="units-count">
                {units.filter(u => u.status === 'Vacant-Ready').length} Vacant-Ready / {units.length} total
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAllUnits}
                  onChange={(e) => setShowAllUnits(e.target.checked)}
                  data-testid="show-all-toggle"
                />
                Show all units
              </label>
            </div>
          </div>
          
          {units.length > 0 ? (
            <Accordion type="multiple" className="buildings-accordion">
              {Object.entries(unitsByAddress).map(([address, addressUnits]) => {
                const displayUnits = getDisplayUnits(addressUnits);
                const vacantReadyCount = addressUnits.filter(u => u.status === 'Vacant-Ready').length;
                
                // Skip buildings with no vacant-ready units if not showing all
                if (!showAllUnits && displayUnits.length === 0) {
                  return null;
                }
                
                return (
                  <AccordionItem key={address} value={address} data-testid={`building-${address}`}>
                    <AccordionTrigger className="building-trigger">
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingRight: '16px' }}>
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>{address}</span>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>
                          {vacantReadyCount > 0 && (
                            <span style={{ color: '#059669', fontWeight: '600', marginRight: '8px' }}>
                              {vacantReadyCount} Vacant-Ready
                            </span>
                          )}
                          {showAllUnits && `• ${addressUnits.length} total`}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <table className="units-table">
                        <thead>
                          <tr>
                            <th>Unit</th>
                            <th>BD/BA</th>
                            <th>Status</th>
                            <th>Sqft</th>
                            <th>Listing</th>
                            <th>Last Updated</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayUnits.map((unit) => (
                            <tr key={unit.id} data-testid={`unit-row-${unit.id}`}>
                              <td>{unit.unit}</td>
                              <td>{unit.bd_ba}</td>
                              <td>
                                <span className={`status-badge status-${unit.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                  {unit.status}
                                </span>
                              </td>
                              <td>{unit.sqft}</td>
                              <td>
                                {unit.listing_url ? (
                                  <a
                                    href={unit.listing_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-action"
                                    style={{ 
                                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                      textDecoration: 'none',
                                      fontSize: '12px',
                                      padding: '4px 8px'
                                    }}
                                    data-testid={`view-listing-${unit.id}`}
                                  >
                                    View Listing
                                  </a>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>
                                )}
                              </td>
                              <td>{new Date(unit.last_updated).toLocaleDateString()}</td>
                              <td>
                                <button
                                  className="btn-action"
                                  onClick={() => openRequestModal(unit)}
                                  data-testid={`request-change-${unit.id}`}
                                >
                                  Request Change
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <div className="empty-state" data-testid="empty-units">
              <p>No units available yet.</p>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="requests">
            {/* My Requests */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>My Status Change Requests</h2>
              </div>
              
              {myRequests.length > 0 ? (
                <table className="units-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>New Status</th>
                      <th>Tenant</th>
                      <th>Rent</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Reviewed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myRequests.map((request) => (
                      <tr key={request.id} data-testid={`request-row-${request.id}`}>
                        <td>{request.unit_info.unit}</td>
                        <td>{request.new_status}</td>
                        <td>{request.tenant_name}</td>
                        <td>${request.rent_amount}</td>
                        <td>
                          <span className={`status-badge status-${request.request_status}`}>
                            {request.request_status}
                          </span>
                        </td>
                        <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                        <td>{request.reviewed_at ? new Date(request.reviewed_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state" data-testid="empty-requests">
                  <p>You haven't submitted any status change requests yet.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            {/* Notification Settings */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Notification Settings</h2>
              </div>
              
              <div style={{ maxWidth: '600px' }}>
                <p style={{ marginBottom: '24px', color: '#64748b', fontSize: '14px' }}>
                  Manage your email notification preferences. You'll receive emails at {user.email}
                </p>

                <div className="notification-setting">
                  <div className="notification-setting-info">
                    <h3>Request Status Updates</h3>
                    <p>Get notified when your status change requests are approved or declined</p>
                  </div>
                  <Switch
                    checked={notifyRequestStatus}
                    onCheckedChange={setNotifyRequestStatus}
                    data-testid="notify-request-status-toggle"
                  />
                </div>

                <div className="notification-setting">
                  <div className="notification-setting-info">
                    <h3>Vacant-Ready Units</h3>
                    <p>Get notified when units become available (Vacant-Ready status)</p>
                  </div>
                  <Switch
                    checked={notifyVacantReady}
                    onCheckedChange={setNotifyVacantReady}
                    data-testid="notify-vacant-ready-toggle"
                  />
                </div>

                <button
                  className="btn-primary"
                  onClick={handleUpdateNotifications}
                  style={{ marginTop: '32px' }}
                  data-testid="save-notifications-button"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      )}

      {/* Request Change Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent data-testid="request-modal">
          <DialogHeader>
            <DialogTitle>Request Status Change</DialogTitle>
          </DialogHeader>
          {selectedUnit && (
            <form onSubmit={handleSubmitRequest}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
                <div className="form-group">
                  <Label>Unit</Label>
                  <Input value={`${selectedUnit.unit} - ${selectedUnit.address || 'No address'}`} disabled />
                </div>
                
                <div className="form-group">
                  <Label htmlFor="newStatus">New Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lease-in-Progress">Lease-in-Progress</SelectItem>
                      <SelectItem value="Current">Current</SelectItem>
                      <SelectItem value="Vacant-Ready">Vacant-Ready</SelectItem>
                      <SelectItem value="Vacant-unturned">Vacant-unturned</SelectItem>
                      <SelectItem value="Offline">Offline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="tenantName">Tenant Name</Label>
                  <Input
                    id="tenantName"
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    placeholder="Enter tenant name"
                    required
                    data-testid="tenant-name-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="rentAmount">Rent Amount ($)</Label>
                  <Input
                    id="rentAmount"
                    type="number"
                    step="0.01"
                    value={rentAmount}
                    onChange={(e) => setRentAmount(e.target.value)}
                    placeholder="Enter rent amount"
                    required
                    data-testid="rent-amount-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="moveInDate">Requested Move-in Date</Label>
                  <Input
                    id="moveInDate"
                    type="date"
                    value={moveInDate}
                    onChange={(e) => setMoveInDate(e.target.value)}
                    required
                    data-testid="move-in-date-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="subsidyProgram">Subsidy Program (Optional)</Label>
                  <Input
                    id="subsidyProgram"
                    value={subsidyProgram}
                    onChange={(e) => setSubsidyProgram(e.target.value)}
                    placeholder="e.g., Section 8, Housing Choice Voucher"
                    data-testid="subsidy-program-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-request-button">Submit Request</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
